'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Award, Download, Loader, CheckCircle, AlertCircle, X as CloseIcon, Clock, MessageCircle, Mail, User, Eye } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/context/ToastContext';
import { createClient } from '@/lib/supabase/client';
import { analytics } from '@/lib/analytics';

type CertificateType = 'participant' | 'speaker';

interface CertificateGeneratorProps {
  courseId: string;
  courseName: string;
  onClose: (dataChanged?: boolean) => void;
  regenerate?: boolean;
  certificateType?: CertificateType;
  enrollmentId?: string | null;
}

interface GeneratedCertificate {
  certificateId: string;
  certificateNumber: string;
  certificateUrl?: string | null;
  verificationCode: string;
  isNew: boolean;
  jobId?: string;
}

export default function CertificateGenerator({
  courseId,
  courseName,
  onClose,
  regenerate = false,
  certificateType,
  enrollmentId,
}: CertificateGeneratorProps) {
  const { t, language } = useLanguage();
  const toast = useToast();
  const supabase = createClient();

  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState<GeneratedCertificate | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isWarning, setIsWarning] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Report template state
  const [reportSending, setReportSending] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [organizerInfo, setOrganizerInfo] = useState<{ name: string; email: string } | null>(null);
  const [organizerLoading, setOrganizerLoading] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const generatedRef = useRef(false);

  // Entrance animation + cleanup
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
    };
  }, []);

  // Poll for PDF job completion
  const pollJobStatus = useCallback(async (jobId: string, certId: string, attempt: number = 0): Promise<void> => {
    const maxAttempts = 60;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      // Still not done after two minutes: the retry cron owns it from here.
      setIsWarning(true);
      setError(t('certificate.renderDelayed'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/pdf-jobs/${jobId}`);
      if (!response.ok) {
        pollingIntervalRef.current = setTimeout(() => pollJobStatus(jobId, certId, attempt + 1), pollInterval);
        return;
      }

      const data = await response.json();

      if (data.status === 'completed' && data.pdf_url) {
        setPdfUrl(data.pdf_url);
        setLoading(false);

        // Update the database
        await supabase
          .from('certifications')
          .update({ certificate_url: data.pdf_url })
          .eq('id', certId);

        toast.success(t('certificates.certificateReady'));
      } else if (data.status === 'failed') {
        // The retry cron re-enqueues failed renders; tell the truth, calmly.
        setIsWarning(true);
        setError(t('certificate.renderDelayed'));
        setLoading(false);
      } else {
        pollingIntervalRef.current = setTimeout(() => pollJobStatus(jobId, certId, attempt + 1), pollInterval);
      }
    } catch (err) {
      console.error('Error polling job status:', err);
      pollingIntervalRef.current = setTimeout(() => pollJobStatus(jobId, certId, attempt + 1), pollInterval);
    }
  }, [supabase, t, toast]);

  // Generate certificate immediately on mount
  useEffect(() => {
    if (generatedRef.current) return;
    generatedRef.current = true;

    const generate = async () => {
      try {
        const response = await fetch('/api/certificates/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ courseId, regenerate, ...(certificateType && { certificateType }) }),
        });

        const data = await response.json();

        if (response.ok) {
          setCertificate(data);

          analytics.certificate.generated({
            content_id: courseId,
            certificate_id: data.certificateId,
            certificate_number: data.certificateNumber,
            generation_time_ms: 0,
            retry_count: 0,
          });

          if (data.certificateUrl) {
            // PDF already available (cached/existing)
            setPdfUrl(data.certificateUrl);
            setLoading(false);
            toast.success(t('certificates.certificateReady'));
          } else if (data.jobId) {
            // PDF being generated async — keep spinner, poll for completion
            pollJobStatus(data.jobId, data.certificateId);
          } else {
            // In flight without a job id (pre-Task-3 server): never a blank modal.
            setIsWarning(true);
            setError(t('certificate.renderGenerating'));
            setLoading(false);
          }
        } else {
          // Map server errors to translated messages
          if (data.error === 'TEMPLATE_NOT_CONFIGURED') {
            setIsWarning(true);
            setError(t('certificate.templateNotConfigured'));
          } else if (data.error === 'TICKET_NO_CERTIFICATE_ACCESS') {
            setError(t('certificates.ticketNoCertificateAccess'));
          } else if (data.error === 'CERTIFICATE_CREATION_FAILED') {
            setError(t('certificates.creationFailed'));
          } else {
            // Server returns human-readable English error strings for tests/surveys/event/profile
            setError(data.error || t('certificates.failedToGenerate'));
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error generating certificate:', err);
        setError(t('certificates.failedToGenerateTryAgain'));
        setLoading(false);
      }
    };

    generate();
  }, [courseId, regenerate, certificateType, pollJobStatus, t, toast]);

  const handleView = () => {
    if (!pdfUrl) return;

    analytics.certificate.downloaded({
      certificate_id: certificate!.certificateId,
      content_id: courseId,
    });

    if (enrollmentId) {
      fetch('/api/enrollment-tracking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enrollment_id: enrollmentId,
          milestone: 'certificate_obtained',
          activity: 'certificate_viewed',
        }),
      }).catch(err => console.error('Failed to track certificate view:', err));
    }

    window.open(pdfUrl, '_blank');
  };

  const handleDownload = async () => {
    if (!pdfUrl || !certificate) return;

    analytics.certificate.downloaded({
      certificate_id: certificate.certificateId,
      content_id: courseId,
    });

    if (enrollmentId) {
      fetch('/api/enrollment-tracking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enrollment_id: enrollmentId,
          milestone: 'certificate_obtained',
          activity: 'certificate_viewed',
        }),
      }).catch(err => console.error('Failed to track certificate download:', err));
    }

    // Real file download via blob
    try {
      setDownloading(true);
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${certificate.certificateNumber || 'download'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed, opening in new tab:', err);
      window.open(pdfUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenReportDialog = async () => {
    setReportDialogOpen(true);

    if (!organizerInfo && !organizerLoading) {
      setOrganizerLoading(true);
      try {
        const { data: content } = await supabase
          .from('content')
          .select('organizer_id, organizers:organizer_profiles (name, email)')
          .eq('id', courseId)
          .single();

        const organizer = content?.organizers as { name: string; email: string } | null;
        if (organizer?.email) {
          setOrganizerInfo({
            name: organizer.name || organizer.email,
            email: organizer.email,
          });
        }
      } catch (error) {
        console.error('Error fetching organizer info:', error);
      } finally {
        setOrganizerLoading(false);
      }
    }
  };

  const handleConfirmReport = async () => {
    try {
      setReportSending(true);
      const response = await fetch('/api/certificates/report-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseId, courseName }),
      });

      if (response.ok) {
        toast.success(t('certificates.reportSent'));
        setReportDialogOpen(false);
      } else {
        toast.error(t('certificates.reportFailed'));
      }
    } catch (error) {
      console.error('Error reporting template:', error);
      toast.error(t('certificates.reportFailed'));
    } finally {
      setReportSending(false);
    }
  };

  const hasResult = !!pdfUrl || !!error;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="certificate-modal-title"
    >
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => onClose(hasResult)}
        aria-hidden="true"
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-lg bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
              <Award className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <h2 id="certificate-modal-title" className="text-base font-semibold text-gray-900">
                  {pdfUrl ? t('certificates.yourCertificate') : t('certificate.certificateOfCompletion')}
                </h2>
                <p className="text-gray-500 text-sm truncate" title={courseName}>{courseName}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => onClose(hasResult)}
              className="p-2 h-auto text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <CloseIcon className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-6">
            {/* Loading — single spinner for both generation + PDF rendering */}
            {loading && (
              <div className="text-center py-8">
                <Loader className="h-10 w-10 text-blue-600 mx-auto mb-3 animate-spin" />
                <p className="text-gray-600">{t('certificate.generatingCertificate')}</p>
              </div>
            )}

            {/* Success — View + Download */}
            {pdfUrl && !loading && (
              <div>
                <div className="text-center mb-6">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('certificates.certificateReady')}
                  </h3>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    onClick={handleView}
                    className="flex-1"
                    size="lg"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {t('profile.certificates.view')}
                  </Button>
                  <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    size="lg"
                  >
                    {downloading ? (
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {t('profile.certificates.download')}
                  </Button>
                </div>

                {/* Report Wrong Template — trigger */}
                {!reportDialogOpen && (
                  <button
                    onClick={handleOpenReportDialog}
                    className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {t('certificates.reportWrongTemplate')}
                  </button>
                )}

                {/* Report Confirmation Dialog */}
                {reportDialogOpen && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-5 space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      {t('certificates.reportDialogTitle')}
                    </h4>

                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('certificates.reportDialogBody')}
                    </p>

                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('certificates.reportDialogCta')}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <Button
                        onClick={handleConfirmReport}
                        disabled={reportSending}
                        size="sm"
                      >
                        {reportSending ? (
                          <>
                            <Loader className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            {t('certificates.reportSending')}
                          </>
                        ) : (
                          <>
                            <Mail className="h-3.5 w-3.5 mr-1.5" />
                            {t('certificates.reportDialogConfirm')}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setReportDialogOpen(false)}
                        disabled={reportSending}
                        size="sm"
                      >
                        {t('certificates.reportDialogCancel')}
                      </Button>
                    </div>

                    {/* Organizer contact info */}
                    <div className="border-t border-amber-200/60 pt-3">
                      <p className="text-xs text-gray-500 mb-2">
                        {t('certificates.reportDialogContactAlt')}
                      </p>
                      {organizerLoading ? (
                        <p className="text-xs text-gray-400">{t('certificates.reportDialogLoadingOrganizer')}</p>
                      ) : organizerInfo ? (
                        <div className="flex items-start gap-2 text-sm">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-800">{organizerInfo.name}</p>
                            <a
                              href={`mailto:${organizerInfo.email}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {organizerInfo.email}
                            </a>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {error && !loading && (
              <div>
                <div className={`rounded-lg p-4 ${isWarning
                    ? 'bg-amber-50 border border-amber-200'
                    : 'bg-red-50 border border-red-200'
                  }`}>
                  <div className="flex items-start gap-3">
                    {isWarning ? (
                      <Clock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <p className={`text-sm ${isWarning ? 'text-amber-800' : 'text-red-800'}`}>{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
